import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, LogOut, Menu, Sparkles, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { MarcaMiolos } from '@/components/Logo';
import { BotaoTema } from '@/tema/BotaoTema';
import { useAuth } from '@/auth/AuthContext';
import { useAcesso } from '@/auth/EstadoAcesso';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

interface ItemDeMenu {
  to: string;
  rotulo: string;
}

/**
 * Itens do menu, na ordem em que aparecem.
 *
 * A lista é montada uma vez e serve os dois lugares: a barra do computador e o
 * menu do celular. Assim os dois nunca divergem — era o risco de escrever a
 * lista duas vezes.
 */
function itensDeMenu(usuario: ReturnType<typeof useAuth>['usuario'], bloqueado: boolean): ItemDeMenu[] {
  const itens: ItemDeMenu[] = [];
  /*
   * Os dois produtos são separados: o módulo de miolos (agenda) e o módulo de
   * documentos (editor acadêmico). O administrador liga e desliga cada um; aqui
   * o menu respeita — quem só tem documentos não vê a agenda, e vice-versa.
   */
  const temMiolos = usuario?.moduloMiolos !== false;

  if (temMiolos) {
    if (usuario && bloqueado) {
      // Acesso bloqueado: sem atalho de criação, só a área com o que já existe.
      itens.push({ to: '/meus-miolos', rotulo: 'Meus miolos' });
    } else {
      itens.push({ to: '/criar', rotulo: 'Criar miolo' });
      if (usuario) itens.push({ to: '/meus-miolos', rotulo: 'Meus miolos' });
    }
  }

  if (usuario?.promptsIA || usuario?.admin) itens.push({ to: '/prompts', rotulo: 'Prompts de IA' });
  // Editor de documentos: liberação nominal do administrador, como o bônus dos
  // prompts. Esconder o link não é segurança — a trava é do servidor.
  if (usuario?.editorDeDocumentos || usuario?.admin) {
    itens.push({ to: '/documentos', rotulo: 'Documentos' });
  }
  if (temMiolos) itens.push({ to: '/modelos', rotulo: 'Modelos prontos' });
  itens.push({ to: '/planos', rotulo: 'Planos' });
  // A biblioteca é pública: visitante lê os resumos sem entrar.
  itens.push({ to: '/biblioteca', rotulo: 'Biblioteca' });
  // Editor de texto livre: área exclusiva, liberada por convite (o servidor é a trava).
  if (usuario?.editorDeTextos || usuario?.admin) itens.push({ to: '/textos', rotulo: 'Meus textos' });
  if (!usuario && temMiolos) itens.push({ to: '/demo', rotulo: 'Demonstração' });
  itens.push({ to: '/contato', rotulo: 'Contato' });
  if (usuario) itens.push({ to: '/conta', rotulo: 'Minha conta' });
  if (usuario?.admin) itens.push({ to: '/admin', rotulo: 'Admin do sistema' });

  return itens;
}

export function Layout() {
  const { usuario, sair } = useAuth();
  /** Acesso bloqueado: a pessoa consulta o que já criou, mas não cria nada. */
  const { bloqueado } = useAcesso();
  const navegar = useNavigate();
  const [menuAberto, setMenuAberto] = useState(false);
  const [manutencao, setManutencao] = useState<{ ativa: boolean; mensagem: string } | null>(null);

  useEffect(() => {
    void api.estadoDaManutencao().then(setManutencao).catch(() => undefined);
  }, []);

  // Em manutenção, quem não é administrador vê só o aviso — o admin entra para desligar.
  if (manutencao?.ativa && !usuario?.admin) {
    return (
      <div className="fundo-app flex min-h-screen flex-col items-center justify-center px-4 text-center">
        <MarcaMiolos tamanho={72} />
        <h1 className="mt-4 text-2xl font-semibold tracking-tight">Em manutenção</h1>
        <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
          {manutencao.mensagem || 'Estamos fazendo melhorias. Volte em alguns minutos.'}
        </p>
      </div>
    );
  }

  const itens = itensDeMenu(usuario, bloqueado);

  const sairDaConta = () => {
    setMenuAberto(false);
    sair();
    navegar('/');
  };

  return (
    <div className="fundo-app flex min-h-screen flex-col">
      <header className="sticky top-0 z-40 border-b border-border/80 bg-background/85 backdrop-blur">
        {/* A linha de cor que abre o site: cinco tons, do lilás ao rosa. */}
        <div aria-hidden className="barra-degrade h-1 w-full" />
        <div className="mx-auto flex h-14 w-full max-w-[1600px] min-w-0 items-center gap-2 px-3 sm:gap-4 sm:px-4">
          {/*
            Menu do celular: a barra de links não cabe em tela estreita.

            O corte é em `xl` (1280) e não em `md` (768) porque foi medido: com a barra
            na tela a partir de 768, a linha do cabeçalho fica **28 px mais larga que a
            janela em 1024** — e aí o site inteiro passa a rolar de lado (acontecia em
            todas as páginas, no editor inclusive). Em 1280 sobra largura de verdade.
          */}
          <Button
            variant="fantasma"
            tamanho="icone"
            className="xl:hidden"
            onClick={() => setMenuAberto(true)}
            aria-label="Abrir menu"
            aria-expanded={menuAberto}
          >
            <Menu />
          </Button>

          <Link to="/" className="flex shrink-0 items-center gap-2 font-semibold tracking-tight">
            <MarcaMiolos tamanho={30} />
            <span className="hidden sm:inline">Miolix</span>
          </Link>

          <nav className="hidden items-center gap-1 text-sm xl:flex">
            {itens
              .filter((item) => item.to !== '/conta')
              .map((item) => (
                <ItemNav key={item.to} to={item.to}>
                  {item.rotulo}
                </ItemNav>
              ))}
          </nav>

          <div className="ml-auto flex min-w-0 items-center gap-1 sm:gap-2">
            <BotaoTema />
            {usuario ? (
              <>
                <Link
                  to="/conta"
                  title="Minha conta"
                  className="hidden items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground sm:flex"
                >
                  <UserRound className="size-3.5" />
                  <span className="hidden max-w-[10rem] truncate lg:inline">{usuario.nome}</span>
                </Link>
                <Button variant="fantasma" tamanho="pequeno" onClick={sairDaConta} className="hidden sm:flex">
                  <LogOut />
                  Sair
                </Button>
              </>
            ) : (
              <>
                <Button asChild variant="fantasma" tamanho="pequeno" className="hidden sm:flex">
                  <Link to="/entrar">Entrar</Link>
                </Button>
                <Button asChild tamanho="pequeno">
                  <Link to="/criar">
                    <Sparkles />
                    <span className="hidden sm:inline">Criar miolo</span>
                    <span className="sm:hidden">Criar</span>
                  </Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Menu do celular: os mesmos itens da barra, empilhados e com alvo grande. */}
      <Dialog open={menuAberto} onOpenChange={setMenuAberto}>
        <DialogContent
          titulo="Menu"
          descricao={usuario ? `Você está na conta ${usuario.nome}.` : undefined}
          className="inset-x-0 top-0 max-h-[92dvh] w-full max-w-none translate-x-0 translate-y-0 overflow-y-auto rounded-t-none pb-6"
        >
          <nav className="flex flex-col">
            {itens.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMenuAberto(false)}
                className={({ isActive }) =>
                  cn(
                    'rounded-md px-3 py-3 text-base font-medium transition-colors',
                    isActive ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground',
                  )
                }
              >
                {item.rotulo}
              </NavLink>
            ))}

            {usuario ? (
              <button
                type="button"
                onClick={sairDaConta}
                className="mt-2 flex items-center gap-2 rounded-md px-3 py-3 text-left text-base font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                <LogOut className="size-4" />
                Sair da conta
              </button>
            ) : (
              <NavLink
                to="/entrar"
                onClick={() => setMenuAberto(false)}
                className="mt-2 rounded-md px-3 py-3 text-base font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                Entrar
              </NavLink>
            )}
          </nav>
        </DialogContent>
      </Dialog>

      <main className="mx-auto w-full max-w-[1600px] min-w-0 flex-1 px-3 py-5 sm:px-4 sm:py-6">
        <Outlet />
      </main>

      <footer className="secao-tingida mt-6 border-t border-border/80 px-4 py-6 text-center text-xs text-muted-foreground" style={{ '--tom': 'var(--cor-alegre-4)' } as React.CSSProperties}>
        <div className="mb-3 flex items-center justify-center gap-2">
          <MarcaMiolos tamanho={26} />
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-foreground/70">
            Miolix
          </span>
        </div>
        <p>
          A plataforma ágil para organizar, gerar e controlar miolos de papelaria personalizada · A4, A5 e
          A6 · impressão pelo navegador
        </p>
        <p className="mt-1 flex items-center justify-center gap-1.5">
          <LayoutDashboard className="size-3" />
          Todo o conteúdo do dia é original, escrito para o Miolix.
        </p>
      </footer>
    </div>
  );
}

function ItemNav({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors',
          isActive
            ? 'bg-secondary text-foreground shadow-[inset_0_-2px_0_0_var(--cor-alegre-3)]'
            : 'text-muted-foreground hover:text-foreground',
        )
      }
    >
      {children}
    </NavLink>
  );
}
